import type { ReactNode, SelectHTMLAttributes } from 'react'
import { periodRangeLabel } from '../../lib/payPeriods'
import type { ReportPreset, ReportRange } from './useReportRange'

export const SELECT_CLASS = 'min-h-11 w-full rounded-control border border-line-strong bg-canvas px-3 text-small'

/**
 * The report controls' select. Its arrow, like every select's, comes from
 * index.css.
 */
export function ReportSelect({ className = '', children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={`${SELECT_CLASS} ${className}`}>
      {children}
    </select>
  )
}

export type ReportToggle = {
  id: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  // Shown among the filters in force while the toggle is away from its
  // default; null while it is at its default.
  activeLabel: string | null
}

export type ReportFilter = {
  id: string
  label: string
  allLabel: string
  // The selected option's value; empty when the filter is not set.
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-caption font-bold text-ink-muted">
      {label}
      {children}
    </label>
  )
}

/** The date range and pay-period fields every range-based report carries. */
export function RangeFields({ range }: { range: ReportRange }) {
  return (
    <>
      <Field label="Date range">
        <ReportSelect value={range.preset} onChange={(event) => range.applyPreset(event.target.value as ReportPreset)}>
          <option value="this">This pay period</option>
          <option value="next">Next pay period</option>
          <option value="last-six">Last six pay periods</option>
          <option value="custom">Custom range</option>
        </ReportSelect>
      </Field>
      <Field label="Pay period">
        <ReportSelect value={range.selectedPeriod ?? ''} onChange={(event) => range.applyPeriod(Number(event.target.value))}>
          <option value="" disabled>
            {range.preset === 'last-six' ? 'Six periods' : 'Custom'}
          </option>
          {range.periodChoices.map((number) => (
            <option key={number} value={number}>
              Pay Period {number}, {periodRangeLabel(number)}
            </option>
          ))}
        </ReportSelect>
      </Field>
      <Field label="From">
        <input type="date" value={range.from} onChange={(event) => range.setFrom(event.target.value)} className={SELECT_CLASS} />
      </Field>
      <Field label="To">
        <input type="date" value={range.to} onChange={(event) => range.setTo(event.target.value)} className={SELECT_CLASS} />
      </Field>
    </>
  )
}

/**
 * The controls every report carries: the date range and pay-period selector,
 * one select for each filter the report offers, the list of filters in force,
 * and a reset. A report supplies its own filters; the range and the chrome
 * are the same everywhere.
 */
export default function ReportControls({
  range,
  rangeFields,
  filters,
  search,
  toggles = [],
  onReset,
}: {
  // A report about a point in time, such as the roster, has no date range.
  range?: ReportRange
  // A report with its own way to choose dates, such as a month, supplies the
  // fields in place of the pay-period range.
  rangeFields?: ReactNode
  filters: ReportFilter[]
  // A report that lists people can search them by name or identifier.
  search?: { value: string; onChange: (value: string) => void }
  // On and off switches, such as whether to include detailed-out personnel.
  toggles?: ReportToggle[]
  onReset: () => void
}) {
  const active = [
    ...filters.flatMap((filter) => {
      const option = filter.options.find((candidate) => candidate.value === filter.value)
      return filter.value && option ? [{ id: filter.id, label: `${filter.label}: ${option.label}` }] : []
    }),
    ...(search?.value ? [{ id: 'search', label: `Search: ${search.value}` }] : []),
    ...toggles.flatMap((toggle) => (toggle.activeLabel ? [{ id: toggle.id, label: toggle.activeLabel }] : [])),
  ]

  return (
    <section aria-label="Report controls" className="rounded-card border border-line bg-canvas p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rangeFields ?? (range && <RangeFields range={range} />)}
        {filters.map((filter) => (
          <Field key={filter.id} label={filter.label}>
            <ReportSelect value={filter.value} onChange={(event) => filter.onChange(event.target.value)}>
              <option value="">{filter.allLabel}</option>
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </ReportSelect>
          </Field>
        ))}
        {search && (
          <Field label="Employee search">
            <input
              type="search"
              value={search.value}
              onChange={(event) => search.onChange(event.target.value)}
              placeholder="Name or identifier"
              className={SELECT_CLASS}
            />
          </Field>
        )}
      </div>

      {toggles.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1">
          {toggles.map((toggle) => (
            <label key={toggle.id} className="flex min-h-11 items-center gap-2 text-small font-semibold">
              <input
                type="checkbox"
                checked={toggle.checked}
                onChange={(event) => toggle.onChange(event.target.checked)}
                className="size-5"
              />
              {toggle.label}
            </label>
          ))}
        </div>
      )}

      {range?.invalid && (
        <p role="alert" className="mt-3 text-small text-error">
          The end date is before the start date. Choose an end date on or after the start.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <p className="text-caption font-bold text-ink-muted">Active filters</p>
        {active.length === 0 ? (
          <span className="text-small text-ink-muted">None</span>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {active.map((filter) => (
              <li key={filter.id} className="rounded-control border border-line-strong bg-surface-2 px-2.5 py-1 text-small">
                {filter.label}
              </li>
            ))}
          </ul>
        )}
        {active.length > 0 && (
          <button
            type="button"
            onClick={onReset}
            className="min-h-11 rounded-control px-3 text-small font-bold text-primary hover:bg-surface-2"
          >
            Reset filters
          </button>
        )}
      </div>
    </section>
  )
}
